/**
 * Files into a page: paste or drop an image (or any file), or pick one with
 * /afbeelding or /bestand. The app stores it as an attachment and the page gets
 * `![[name]]`, the same embed imported Obsidian attachments use.
 */
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorHost } from './host';

export async function insertFiles(
  editor: Editor,
  host: EditorHost,
  files: File[],
  pos?: number,
): Promise<void> {
  if (!host.uploadFile) return;
  let at = pos ?? editor.state.selection.to;
  for (const file of files) {
    const target = await host.uploadFile(file);
    if (!target) continue;
    const embed = { type: 'embed', attrs: { target } };
    editor.chain().focus().insertContentAt(at, embed).run();
    at = editor.state.selection.to;
  }
}

/** Ask for files (the /afbeelding and /bestand commands). */
export function pickFiles(editor: Editor, host: EditorHost, accept: string) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.multiple = true;
  input.onchange = () => void insertFiles(editor, host, [...(input.files ?? [])]);
  input.click();
}

export function createFileDrop(host: EditorHost) {
  return Extension.create({
    name: 'fileDrop',
    addProseMirrorPlugins() {
      const editor = this.editor;
      return [
        new Plugin({
          key: new PluginKey('fileDrop'),
          props: {
            handlePaste(_view, event) {
              const files = [...(event.clipboardData?.files ?? [])];
              if (!files.length || !host.uploadFile) return false;
              event.preventDefault();
              void insertFiles(editor, host, files);
              return true;
            },
            handleDrop(view, event, _slice, moved) {
              const files = [...(event.dataTransfer?.files ?? [])];
              if (moved || !files.length || !host.uploadFile) return false;
              event.preventDefault();
              const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
              void insertFiles(editor, host, files, pos);
              return true;
            },
          },
        }),
      ];
    },
  });
}
